import {Box, makeStyles, Tooltip, Typography, withStyles} from "@material-ui/core";
import {SecondaryButton} from "Components/Button";
import clsx from "clsx";
import {LockIcon, MultiplayerIcon, TicketIcon, TrophyIcon} from "Components/CustomIcons";
import ReactSVG from 'react-svg'
import {BP_LARGE, BP_MEDIUM, BP_SMALL} from "Theme/breakpoints";
import {ANIMATED_BINGO_CARD_ENABLED, BINGO_MAINTENANCE, SHOW_PJP_ON_BINGO_CARDS} from "Setup/config";
import useGames from "Hooks/useGames";
import {Skeleton} from "@material-ui/lab";
import WebpImage from "Components/WebpImage";
import BingoCardPJPBar from "Components/Bingo/BingoCard/BingoCardPJPBar";
import {BingoCountdown} from "Components/Bingo/BingoCountdown";
import {getRoomImageFromRoomObject} from "Components/Bingo/helpers";
import ErrorBoundary from "Components/ErrorBoundary";
import {useEffect, useState} from "react";

const useBingoCardStyles = makeStyles(theme => {
    const bingoCardsSettings = theme.overrides.BingoCards;

    return {
        root: {
            fontFamily: bingoCardsSettings.fontFamily,
        },
        lockOverlay: {
            position: "absolute",
            top: 30,
            left: 0,
            right: 0,
            color: "#fff",
            zIndex: 1,
            width: "100%",
            fontSize: theme.spacing(8),
            paddingLeft: 15,
            [theme.breakpoints.down(BP_SMALL)]: {
                top: 15
            },
        },
        closed: {
            '& $imageContainer': {
                '& svg:not($lockOverlay), & img': {
                    filter: "brightness(0.4)"
                }
            }
        },
        imageContainer: {
            position: "relative",
            display: "flex",
            minHeight: 110,
            [theme.breakpoints.up(BP_LARGE)]: {
                minHeight: 134
            },
            background: bingoCardsSettings.background,
        },
        bingoRoomSvg: {
            minHeight: 110,
            '& svg': {
                width: "100%",
                display: "block",
                height: "auto",
            }
        },
        bingoRoomImg: {
            width: "100%",
            display: "block",
            height: "auto",
            [theme.breakpoints.down(BP_LARGE)]: {
                minHeight: 110
            },
        },
        container: {
            cursor: "default",
            color: bingoCardsSettings.titleColor,
            margin: "0 auto",
            position: "relative",
            height: 320,
            maxWidth: 275,
            background: "white",
            borderRadius: bingoCardsSettings.borderRadius,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow: "0 -1px 1px 0 rgba(0, 0, 0, .05), 0 1px 3px 0 rgba(0, 0, 0, .5)",
            transition: "all .2s ease",
            transform: "translateY(2px)",
            '&:hover': {
                transform: "translateY(0px)",
                boxShadow: "0 4px 15px 0 rgba(0, 0, 0, .3), 0 0 1px 0 rgba(0, 0, 0, .25)"
            },
            [theme.breakpoints.down(BP_LARGE)]: {
                height: 310
            },
            [theme.breakpoints.down(BP_MEDIUM)]: {
                height: 280
            },
        },
        gameType: {
            position: "absolute",
            top: "5px",
            left: "5px",
            color: "black",
            background: "white",
            width: 25,
            height: 25,
            borderRadius: 20,
            fontWeight: "bold",
            zIndex: 1,
            // border: `3px solid black`,
            //
            // '&.ball-90': {
            //     border: `3px solid ${theme.palette.primary.main}`
            // },
            // '&.ball-75': {
            //     border: `3px solid ${theme.palette.secondary.main}`
            // },
            // '&.ball-30': {
            //     border: `3px solid ${theme.palette.primary.main}`
            // }
        },
        timeContainer: {
            position: "absolute",
            top: 8,
            right: 8,
            background: "#fff",
            borderRadius: 50,
            color: "black",
            padding: theme.spacing(0, 0.5),
            zIndex: 1,
            height: 25,
            minWidth: 64,
            '& > .MuiSvgIcon-root': {
                fontSize: theme.spacing(2.5),
            },
        },
        time: {
            width: "100%",
            display: "flex",
            justifyContent: "space-around",
        },
        details: {
            '& .MuiSvgIcon-root': {
                color: bingoCardsSettings.iconColor,
            }
        },
        roomName: {
            fontSize: '1.25em',
        },
        jackpotAmount: {
            fontFamily: bingoCardsSettings.fontFamily,
            fontWeight: theme.typography.fontWeightBold,
            lineHeight: "normal",
            fontSize: 18,
            [theme.breakpoints.up(BP_MEDIUM)]: {
                fontSize: 22,
            },
        },
        cta: {
            width: 120,
            maxWidth: "100%"
        },
        playersContainer: {
            '& > span': {
                paddingLeft: theme.spacing(0.5),
                lineHeight: "normal"
            }
        },
        ticketsContainer: {
            '& > span': {
                paddingLeft: theme.spacing(0.5),
                lineHeight: "normal"
            }
        },
    }
})

export const SkeletonBingoCard = () => {
    return (
        <Box m="auto" maxWidth={270}>
            <Skeleton variant="rect" height={135}/>
            <Box>
                <Skeleton animation="wave" width={'100%'}/>
                <Skeleton animation="wave" width={'75%'}/>
            </Box>
        </Box>
    );
};

const CustomToolTip = withStyles({
    tooltip: {
        margin: 2,
    }
})(Tooltip);

const BingoCardContentImage = ({roomImage, roomName, roomOpen, hasError}) => {
    const classes = useBingoCardStyles();
    const [svgExists, setSVGExists] = useState(null);

    const checkImageExists = async (url) => {
        try {
            const response = await fetch(url);
            if (response.ok) {
                setSVGExists(true);
            } else {
                setSVGExists(false);
            }
        } catch (error) {
            setSVGExists(false);
        }
    };

    useEffect(() => {
        if (roomImage) {
            checkImageExists(`${roomImage}.svg`);
        }
    }, [roomImage]);

    const USE_ANIMATED_IMAGE = ANIMATED_BINGO_CARD_ENABLED && !hasError;

    if ((USE_ANIMATED_IMAGE && svgExists === null)) {
        return <Skeleton variant="rect" height={135}/>;
    }

    return (
        <div className={classes.imageContainer}>
            {(USE_ANIMATED_IMAGE && svgExists) ?
                <ReactSVG className={classes.bingoRoomSvg} src={`${roomImage}.svg`}/> :
                <WebpImage height={136} className={classes.bingoRoomImg} src={`${roomImage}.png`} alt={roomName}/>
            }
            {!roomOpen && <LockIcon className={classes.lockOverlay}/>}
        </div>
    );

}

const BingoCardContents = (props) => {
    const classes = useBingoCardStyles();
    const {launchBingoGame} = useGames();

    let {
        minimal,
        isLoaded,
        gameType,
        roomName,
        gamePrize,
        jackpotAmount,
        playersInRoom,
        cardPrice,
        nextGameCountdownInSeconds,
        image,
        roomOpen,
        currencyMinor,
        currencyMajor,
        catalogueGameId
    } = props;

    if (!isLoaded) {
        return <SkeletonBingoCard/>;
    }

    const handleOnClick = () => {
        if (props.onClick) {
            props.onClick();
        } else {
            launchBingoGame(catalogueGameId)
        }
    };

    return (
        <div className={clsx(classes.container, "bg--bingo-card")}>
            <>
                {roomOpen &&
                    <>
                        {gameType &&
                            <CustomToolTip arrow title={"Game Type"}>
                                <Box display="flex" alignItems="center" justifyContent="center" textAlign="center"
                                     className={`${classes.gameType} ball-${gameType}`}>
                                    {gameType}
                                </Box>
                            </CustomToolTip>
                        }
                        {!!nextGameCountdownInSeconds &&
                            <BingoCountdown
                                visibility={minimal ? 'hidden' : 'visible'}
                                seconds={nextGameCountdownInSeconds}
                                styles={{"position": "absolute", "top": 8, "right": 8}}
                            />
                        }
                    </>
                }

                <ErrorBoundary>
                    <BingoCardContentImage
                        roomName={roomName}
                        roomOpen={roomOpen}
                        roomImage={image}
                    />
                </ErrorBoundary>

                {(SHOW_PJP_ON_BINGO_CARDS) &&
                    <BingoCardPJPBar amount={jackpotAmount ? jackpotAmount : null}/>
                }

                <Box className={classes.details} textAlign="center" display="flex" flexDirection="column" flex={1} alignItems="center"
                     justifyContent="space-evenly">
                    <Typography className={classes.roomName} component="h3" variant="inherit">{roomName}</Typography>
                    {(roomOpen && gamePrize) ?
                        <CustomToolTip arrow placement="top" title={"Game Prize"}>
                            <Box display="flex" justifyContent="center" alignItems="center">
                                <Box px={1} display="flex" alignItems="center"><TrophyIcon/></Box>
                                <Typography className={classes.jackpotAmount}>{currencyMajor}{gamePrize}</Typography>
                            </Box>
                        </CustomToolTip> :
                        <>
                            {!BINGO_MAINTENANCE &&
                                <div className="locked" style={!nextGameCountdownInSeconds ? {visibility: 'hidden'} : {}}>
                                    <em>Room opens in:</em>
                                    <Box display="flex" justifyContent="center" alignItems="center">
                                        <BingoCountdown seconds={nextGameCountdownInSeconds} styles={{"position": "inherit"}}/>
                                    </Box>
                                </div>
                            }
                        </>
                    }
                    <SecondaryButton onClick={handleOnClick} className={classes.cta} variant={roomOpen ? 'contained' : 'outlined'}>
                        {roomOpen ? 'Play Now' : 'Pre Buy'}
                    </SecondaryButton>
                </Box>

                <Box mt="auto" display="flex" justifyContent="space-between" pb={0.5} px={1}>
                    <CustomToolTip arrow title={roomOpen ? "Players in the Room" : ""}>
                        <Box visibility={minimal ? 'hidden' : 'visible'} display="flex" alignItems="center" className={classes.playersContainer}>
                            <MultiplayerIcon style={{visibility: roomOpen && !minimal ? 'visible' : 'hidden'}}/>
                            {roomOpen && <span>{playersInRoom}</span>}
                        </Box>
                    </CustomToolTip>
                    <CustomToolTip arrow title={roomOpen ? "Ticket Price" : ""}>
                        <Box visibility={minimal ? 'hidden' : 'visible'} display="flex" alignItems="center" className={classes.ticketsContainer}>
                            <TicketIcon style={{visibility: roomOpen && !minimal ? 'visible' : 'hidden'}}/>
                            {roomOpen && <span>{cardPrice.toFixed(0)}{currencyMinor}</span>}
                        </Box>
                    </CustomToolTip>
                </Box>
            </>
        </div>
    )
};

export const BingoCard = props => {
    const classes = useBingoCardStyles();
    if (props.hasError) return null;

    return (
        <div className={clsx(classes.bingoCard, {[classes.roomLoading]: !props.isLoaded})}>
            <Box mb={props.marginBottom ? 5 : 0} className={clsx(classes.root, {[classes.closed]: !props.roomOpen})}>
                <BingoCardContents {...props}/>
            </Box>
        </div>
    )
};

export const BingoSpecialsCard = ({room, marginBottom = false, ...rest}) => {
    const classes = useBingoCardStyles();
    const {launchBingoGame} = useGames();

    if (rest.hasError) return null;

    const roomImage = getRoomImageFromRoomObject(room);

    return (
        <Box mb={marginBottom ? 2 : 0} className={clsx(classes.root)}>
            <div className={clsx(classes.container, "bg--bingo-card")}>
                <div className={classes.imageContainer}>
                    <WebpImage className={classes.bingoRoomImg} src={`${roomImage}.png`} alt={room.roomName}/>
                </div>
                <Box className={classes.details} textAlign="center" display="flex" flexDirection="column" flex={1} alignItems="center"
                     justifyContent="space-evenly">
                    <Typography component="h3" variant="inherit">{room.roomName}</Typography>
                    <Box className="locked">
                        <em>Next game:</em>
                        <BingoCountdown date={room.nextGameTimestamp} seconds={room.nextGameSec} styles={{"position": "inherit"}}/>
                    </Box>
                    <SecondaryButton className={classes.cta} variant='outlined' onClick={() => launchBingoGame(room.cgid)}>Pre Buy</SecondaryButton>
                </Box>
                <Box mt="auto" display="flex" justifyContent="space-between" pb={0.5} px={1}>
                    <Box display="flex" alignItems="center" className={classes.playersContainer}>
                        <MultiplayerIcon style={{visibility: 'hidden'}}/>
                    </Box>
                    <Box display="flex" alignItems="center" className={classes.ticketsContainer}>
                        <TicketIcon style={{visibility: 'hidden'}}/>
                    </Box>
                </Box>
            </div>
        </Box>
    )
};
